import { useEffect, useRef, useState } from "react";

import { Flag, type FlagReference } from "@prophero-ds/cx-react";

import "./PropHeroSelectCountry.css";

// Hardcoded labels - only 2 languages to support
const LANGUAGES: ReadonlyArray<{
  value: "en" | "es";
  label: string;
  flag: FlagReference["name"];
}> = [
  { value: "en", label: "English", flag: "united-kingdom" },
  { value: "es", label: "Español", flag: "spain" },
];

interface PropHeroSelectCountryProps {
  direction?: "top" | "bottom";
  currentLanguage: "en" | "es";
  onChange: (event: { language: "en" | "es" }) => void;
}

const PropheroSelectCountry = ({
  direction = "bottom",
  currentLanguage,
  onChange,
}: PropHeroSelectCountryProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        selectRef.current &&
        !selectRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleSelect = (lang: "en" | "es") => {
    onChange({ language: lang });
    setIsOpen(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setIsOpen(!isOpen);
    } else if (event.key === "Escape") {
      setIsOpen(false);
    }
  };

  const selectedLanguage = LANGUAGES.find(
    (lang) => lang.value === currentLanguage
  );

  return (
    <div className="language-select" ref={selectRef}>
      <button
        type="button"
        id="language-select-trigger"
        className="language-select__trigger"
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label="Select language"
      >
        {selectedLanguage && (
          <Flag
            name={selectedLanguage.flag}
            className="language-select__flag"
            aria-hidden
          />
        )}
        {selectedLanguage && (
          <span className="language-select__label">
            {selectedLanguage.label}
          </span>
        )}
        <svg
          className={`language-select__chevron ${isOpen ? "language-select__chevron--up" : ""}`}
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden
        >
          <path
            d="M4 6L8 10L12 6"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {isOpen && (
        <div
          className={`language-select__menu ${direction === "top" ? "language-select__menu--top" : ""}`}
          role="listbox"
        >
          {LANGUAGES.map((lang) => (
            <button
              key={lang.value}
              type="button"
              id={`language-option-${lang.value}`}
              className={`language-select__option ${currentLanguage === lang.value ? "language-select__option--selected" : ""}`}
              onClick={() => handleSelect(lang.value)}
              role="option"
              aria-selected={currentLanguage === lang.value}
            >
              <Flag
                name={lang.flag}
                className="language-select__flag"
                aria-hidden
              />
              <span className="language-select__label">{lang.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default PropheroSelectCountry;
